import React, { useState, useEffect } from 'react';
import { superAdminApiService } from '../services/SuperAdminApiService';
import {
  Mail,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  HelpCircle,
  BarChart3,
  AlertTriangle
} from 'lucide-react';
import './EmailHistory.css';

interface EmailLog {
  id: number;
  destinatario: string;
  oggetto: string;
  stato: 'in_coda' | 'inviata' | 'fallita';
  errore?: string;
  created_at: string;
  sent_at?: string;
  tenant_id: string;
}

interface EmailStats {
  totaleEmail: number;
  emailInviate: number;
  emailFallite: number;
  percentualeSuccesso: number;
  ultimaEmail?: string;
  statisticheTenant: Array<{
    tenant_id: string;
    tenant_nome: string;
    totale: number;
    inviate: number;
    fallite: number;
  }>;
  statisticheGiornaliere: Array<{
    data: string;
    totale: number;
    inviate: number;
    fallite: number;
  }>;
}

interface TenantInfo {
  id: string;
  nome: string;
}

interface EmailHistoryProps {
  tenants: TenantInfo[];
}

const EmailHistory: React.FC<EmailHistoryProps> = ({ tenants }) => {
  const [emailHistory, setEmailHistory] = useState<EmailLog[]>([]);
  const [emailStats, setEmailStats] = useState<EmailStats | null>(null);
  const [selectedTenant, setSelectedTenant] = useState<string>('');
  const [stato, setStato] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const limit = 20;

  useEffect(() => {
    loadEmailStats();
  }, []);

  useEffect(() => {
    loadEmailHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch solo al cambio filtri/pagina; loadEmailHistory è ricreata ad ogni render
  }, [selectedTenant, stato, currentPage]);

  const loadEmailHistory = async () => {
    try {
      setLoading(true);
      const tenantId = selectedTenant || 'all';
      const offset = (currentPage - 1) * limit;
      
      const response = await superAdminApiService.getEmailHistory(tenantId, {
        limit,
        offset,
        stato: stato || undefined
      });
      
      setEmailHistory(response.emails);
      setTotalPages(Math.ceil(response.total / limit));
      setError(null);
    } catch (err: any) {
      setError(`Errore caricamento storico email: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadEmailStats = async () => {
    try {
      const stats = await superAdminApiService.getEmailStats();
      setEmailStats(stats);
    } catch (err: any) {
      console.error('Errore caricamento statistiche email:', err);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('it-IT');
  };

  const getStatusIcon = (stato: string) => {
    const iconStyle = { verticalAlign: 'text-bottom' as const, marginRight: 6 };
    switch (stato) {
      case 'inviata': return <CheckCircle2 size={14} style={iconStyle} />;
      case 'fallita': return <XCircle size={14} style={iconStyle} />;
      case 'in_coda': return <Clock size={14} style={iconStyle} />;
      default: return <HelpCircle size={14} style={iconStyle} />;
    }
  };

  const getStatusColor = (stato: string) => {
    switch (stato) {
      case 'inviata': return '#10b981';
      case 'fallita': return '#ef4444';
      case 'in_coda': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const getTenantName = (tenantId: string) => {
    const tenant = tenants.find(t => t.id === tenantId);
    return tenant ? tenant.nome : tenantId;
  };

  return (
    <div className="email-history">
      <div className="email-history-header">
        <h2>
          <Mail size={20} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
          Storico Email
        </h2>
        <button onClick={() => {
          loadEmailHistory();
          loadEmailStats();
        }} className="refresh-btn">
          <RefreshCw size={16} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
          Aggiorna
        </button>
      </div>

      {emailStats && (
        <div className="email-stats-overview">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon"><Mail size={28} /></div>
              <div className="stat-content">
                <h3>Totale Email</h3>
                <div className="stat-number">{emailStats.totaleEmail}</div>
              </div>
            </div>
            
            <div className="stat-card success">
              <div className="stat-icon"><CheckCircle2 size={28} /></div>
              <div className="stat-content">
                <h3>Email Inviate</h3>
                <div className="stat-number">{emailStats.emailInviate}</div>
              </div>
            </div>
            
            <div className="stat-card error">
              <div className="stat-icon"><XCircle size={28} /></div>
              <div className="stat-content">
                <h3>Email Fallite</h3>
                <div className="stat-number">{emailStats.emailFallite}</div>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon"><BarChart3 size={28} /></div>
              <div className="stat-content">
                <h3>% Successo</h3>
                <div className="stat-number">{emailStats.percentualeSuccesso?.toFixed(1) || '0.0'}%</div>
              </div>
            </div>
          </div>

          {emailStats.statisticheGiornaliere.length > 0 && (
            <div className="daily-stats">
              <h3>Statistiche Ultimi 7 Giorni</h3>
              <div className="daily-stats-grid">
                {emailStats.statisticheGiornaliere.map((day, index) => (
                  <div key={index} className="daily-stat">
                    <div className="day-date">{new Date(day.data).toLocaleDateString('it-IT')}</div>
                    <div className="day-stats">
                      <span className="total">{day.totale} totali</span>
                      <span className="success">{day.inviate} inviate</span>
                      <span className="failed">{day.fallite} fallite</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="email-filters">
        <div className="filter-group">
          <label htmlFor="tenant-filter">Tenant:</label>
          <select 
            id="tenant-filter"
            value={selectedTenant} 
            onChange={(e) => {
              setSelectedTenant(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">Tutti i tenant</option>
            {tenants.map(tenant => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.nome}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="status-filter">Stato:</label>
          <select 
            id="status-filter"
            value={stato} 
            onChange={(e) => {
              setStato(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">Tutti gli stati</option>
            <option value="inviata">Inviate</option>
            <option value="fallita">Fallite</option>
            <option value="in_coda">In coda</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <AlertTriangle size={16} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
          {error}
        </div>
      )}

      {loading ? (
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Caricamento storico email...</p>
        </div>
      ) : (
        <>
          <div className="email-table-container">
            <table className="email-table">
              <thead>
                <tr>
                  <th>Stato</th>
                  <th>Destinatario</th>
                  <th>Oggetto</th>
                  <th>Tenant</th>
                  <th>Creata</th>
                  <th>Inviata</th>
                  <th>Errore</th>
                </tr>
              </thead>
              <tbody>
                {emailHistory.map((email) => (
                  <tr key={email.id}>
                    <td>
                      <span 
                        className="status-badge" 
                        style={{ color: getStatusColor(email.stato) }}
                      >
                        {getStatusIcon(email.stato)} {email.stato}
                      </span>
                    </td>
                    <td>{email.destinatario}</td>
                    <td className="subject-cell" title={email.oggetto}>
                      {email.oggetto.length > 50 
                        ? `${email.oggetto.substring(0, 50)}...` 
                        : email.oggetto
                      }
                    </td>
                    <td>{getTenantName(email.tenant_id)}</td>
                    <td>{formatDate(email.created_at)}</td>
                    <td>{email.sent_at ? formatDate(email.sent_at) : '-'}</td>
                    <td className="error-cell">
                      {email.errore ? (
                        <span className="error-text" title={email.errore}>
                          {email.errore.length > 30
                            ? `${email.errore.substring(0, 30)}...` 
                            : email.errore
                          }
                        </span>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button 
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                ← Precedente
              </button>
              
              <span className="pagination-info">
                Pagina {currentPage} di {totalPages}
              </span>
              
              <button 
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                Successiva →
              </button>
            </div>
          )}

          {emailHistory.length === 0 && !loading && (
            <div className="no-data">
              Nessuna email trovata con i filtri selezionati
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EmailHistory;
